import React, { useState } from 'react';
import { CheckCircle, X } from 'lucide-react';
import Button from '@/components/ui/Button';

interface Conflict {
  id: string;
  entityType: string;
  entityId: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  status: 'open' | 'resolved' | 'ignored';
  detectedAt: string;
  sourceSystem?: {
    data: any;
    lastModified: string;
  };
  platform?: {
    data: any;
    lastModified: string;
  };
  mra?: {
    data: any;
    lastModified: string;
  };
}

interface ConflictResolutionModalProps {
  conflict: Conflict;
  onResolve: (conflictId: string, resolution: 'source' | 'platform' | 'mra' | 'ignore') => void;
  onClose: () => void;
}

const ConflictResolutionModal: React.FC<ConflictResolutionModalProps> = ({
  conflict,
  onResolve,
  onClose,
}) => {
  const [selectedResolution, setSelectedResolution] = useState<'source' | 'platform' | 'mra' | 'ignore'>('platform');
  const [notes, setNotes] = useState('');

  const handleResolve = () => {
    onResolve(conflict.id, selectedResolution);
    onClose();
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'text-red-600 bg-red-50';
      case 'high':
        return 'text-orange-600 bg-orange-50';
      case 'medium':
        return 'text-yellow-600 bg-yellow-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const renderEntityData = (title: string, data: any, lastModified: string) => (
    <div className="border rounded-lg p-4">
      <h4 className="font-medium text-gray-900 mb-2">{title}</h4>
      <div className="space-y-1">
        {Object.entries(data).map(([key, value]) => (
          <div key={key} className="flex justify-between text-sm">
            <span className="text-gray-600">{key}:</span>
            <span className="font-medium">{String(value)}</span>
          </div>
        ))}
      </div>
      <p className="text-xs text-gray-500 mt-2">
        Last modified: {new Date(lastModified).toLocaleString()}
      </p>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Resolve Conflict</h2>
            <div className="flex items-center space-x-2 mt-2">
              <span className={`px-2 py-1 rounded text-xs font-medium ${getSeverityColor(conflict.severity)}`}>
                {conflict.severity.toUpperCase()}
              </span>
              <span className="text-sm text-gray-500">
                {conflict.entityType} • {conflict.entityId}
              </span>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Conflict Details */}
        <div className="mb-6">
          <p className="text-sm text-gray-600">
            Detected {new Date(conflict.detectedAt).toLocaleString()}
          </p>
        </div>

        {/* Side-by-side comparison */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          {conflict.sourceSystem && renderEntityData(
            'Source System',
            conflict.sourceSystem.data,
            conflict.sourceSystem.lastModified
          )}
          {conflict.platform && renderEntityData(
            'Platform',
            conflict.platform.data,
            conflict.platform.lastModified
          )}
          {conflict.mra && renderEntityData(
            'MRA',
            conflict.mra.data,
            conflict.mra.lastModified
          )}
        </div>

        {/* Resolution Options */}
        <div className="border-t pt-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Resolution Strategy</h3>
          <div className="space-y-3">
            <label className="flex items-center space-x-3">
              <input
                type="radio"
                name="resolution"
                value="source"
                checked={selectedResolution === 'source'}
                onChange={(e) => setSelectedResolution(e.target.value as any)}
                className="text-blue-600"
              />
              <span className="text-sm">Use Source System value</span>
            </label>
            <label className="flex items-center space-x-3">
              <input
                type="radio"
                name="resolution"
                value="platform"
                checked={selectedResolution === 'platform'}
                onChange={(e) => setSelectedResolution(e.target.value as any)}
                className="text-blue-600"
              />
              <span className="text-sm">Use Platform value</span>
            </label>
            {conflict.mra && (
              <label className="flex items-center space-x-3">
                <input
                  type="radio"
                  name="resolution"
                  value="mra"
                  checked={selectedResolution === 'mra'}
                  onChange={(e) => setSelectedResolution(e.target.value as any)}
                  className="text-blue-600"
                />
                <span className="text-sm">Use MRA value</span>
              </label>
            )}
            <label className="flex items-center space-x-3">
              <input
                type="radio"
                name="resolution"
                value="ignore"
                checked={selectedResolution === 'ignore'}
                onChange={(e) => setSelectedResolution(e.target.value as any)}
                className="text-blue-600"
              />
              <span className="text-sm">Ignore conflict</span>
            </label>
          </div>

          {/* Notes */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Resolution Notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full p-2 border border-gray-300 rounded-md text-sm"
              placeholder="Add notes about this resolution..."
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3 mt-6">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleResolve}>
            <CheckCircle className="h-4 w-4 mr-2" />
            Resolve Conflict
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ConflictResolutionModal;
